import type { ChecklistItem as LegacyChecklistItem, Goal, SubTask, Task } from '../types/timeline'
import type { TimelineElement } from '../context/TimelineContext'
import type { ChecklistItem as FlatChecklistItem } from '../types/schema'

/**
 * Two data stores hold the goal tree:
 *
 *   1. `users/{uid}/timeline/state.goals` — a single legacy doc with the whole
 *      nested Goal[] tree, exposed via useData().goals. The Manage save flow
 *      keeps this in sync for *manual* edits, but the AI-pending-save flow
 *      writes only to the flat store, leaving this nested doc stale or empty
 *      for AI-generated items.
 *   2. `elements/*` — one doc per Goal/Task/Subtask, exposed via
 *      useTimeline().elements. These TimelineElement docs carry the canonical
 *      `startDate`, `endDate`, `color`, `description`, and `checklist`. Every
 *      Manage / canvas edit writes here.
 *   3. `checklist_items/*` (flat store) — one doc per checklist item, exposed
 *      via useTimeline().checklistItems. Truth for canvas-created checklists;
 *      element.checklist may be empty or stale for those containers, so when
 *      the flat store has any item for a container we source the children
 *      from there instead — mirrors the getBridgeChecklistItems rule in
 *      TimelineContext.
 *
 * The flat store is the source of truth for the canvas, the timeline view,
 * and Manage. This helper materialises the nested Goal[] view from flat
 * elements so the AI panel and Manage's Review tree see the same data
 * canvas/board do.
 *
 * Pure transform — no Firestore round-trip.
 */

const flatToLegacyChecklist = (item: FlatChecklistItem): LegacyChecklistItem => ({
  id: item.id,
  text: item.title ?? '',
  description: item.description,
  completionCriteria: item.completionCriteria,
  isCompleted: Boolean(item.isCompleted),
  priority: typeof item.priority === 'number' ? item.priority : undefined,
  weightage: typeof item.weightage === 'number' && item.weightage > 1 ? item.weightage : 1,
  scheduleStartDate: item.startDate ?? null,
  scheduleEndDate: item.endDate ?? null,
  workflowStatus: item.workflowStatus ?? null,
  attachedFormIds: item.attachedFormIds,
  attachedDesignIds: item.attachedDesignIds,
  attachedUploadIds: item.attachedUploadIds,
  attachedLinkIds: item.attachedLinkIds,
  assigneeId: item.assigneeId ?? null,
})

/**
 * Group flat checklist items by parentContainerId, sorted by canvas Y then X.
 * Recurrence-instance mirrors and soft-deleted items are excluded — the tree
 * shows the definition (parent) rows only.
 */
const groupChecklistsByParent = (
  items: FlatChecklistItem[],
): Map<string, LegacyChecklistItem[]> => {
  const raw = new Map<string, FlatChecklistItem[]>()
  for (const item of items) {
    if (item.deletedAt) continue
    if (item.isRecurrenceInstance) continue
    const bucket = raw.get(item.parentContainerId) ?? []
    bucket.push(item)
    raw.set(item.parentContainerId, bucket)
  }
  const out = new Map<string, LegacyChecklistItem[]>()
  for (const [parentId, bucket] of raw) {
    bucket.sort((a, b) => {
      const ay = a.position?.y ?? 0
      const by = b.position?.y ?? 0
      if (ay !== by) return ay - by
      const ax = a.position?.x ?? 0
      const bx = b.position?.x ?? 0
      if (ax !== bx) return ax - bx
      return (a.title ?? '').localeCompare(b.title ?? '')
    })
    out.set(parentId, bucket.map(flatToLegacyChecklist))
  }
  return out
}

const pickChecklist = (
  elementId: string,
  legacy: LegacyChecklistItem[] | undefined,
  flatByParent: Map<string, LegacyChecklistItem[]> | null,
): LegacyChecklistItem[] => {
  const flat = flatByParent?.get(elementId)
  if (flat && flat.length > 0) return flat
  return legacy ?? []
}

export const buildGoalsFromTimelineElements = (
  elements: TimelineElement[],
  checklistItems: FlatChecklistItem[] = [],
): Goal[] => {
  const goalsRaw = elements.filter((element) => element.type === 'GOAL')
  const tasksByParent = new Map<string, TimelineElement[]>()
  const subTasksByParent = new Map<string, TimelineElement[]>()

  for (const element of elements) {
    if (element.type === 'TASK' && element.parentId) {
      const bucket = tasksByParent.get(element.parentId) ?? []
      bucket.push(element)
      tasksByParent.set(element.parentId, bucket)
    } else if (element.type === 'SUBTASK' && element.parentId) {
      const bucket = subTasksByParent.get(element.parentId) ?? []
      bucket.push(element)
      subTasksByParent.set(element.parentId, bucket)
    }
  }

  const flatByParent = checklistItems.length > 0 ? groupChecklistsByParent(checklistItems) : null

  const buildSubTask = (element: TimelineElement): SubTask => ({
    id: element.id,
    title: element.title,
    description: element.description ?? '',
    startDate: element.startDate ?? '',
    endDate: element.endDate ?? '',
    color: (element.color ?? '#60a5fa') as `#${string}`, // mosaic-keep: data (fallback subtask hex color)
    checklist: pickChecklist(element.id, element.checklist, flatByParent),
  })

  const buildTask = (element: TimelineElement): Task => ({
    id: element.id,
    title: element.title,
    description: element.description ?? '',
    startDate: element.startDate ?? '',
    endDate: element.endDate ?? '',
    color: (element.color ?? '#3b82f6') as `#${string}`, // mosaic-keep: data (fallback task hex color)
    checklist: pickChecklist(element.id, element.checklist, flatByParent),
    subTasks: (subTasksByParent.get(element.id) ?? []).map(buildSubTask),
  })

  return goalsRaw.map((element): Goal => ({
    id: element.id,
    title: element.title,
    description: element.description ?? '',
    startDate: element.startDate ?? '',
    endDate: element.endDate ?? '',
    baseColor: (element.color ?? '#6366f1') as `#${string}`, // mosaic-keep: data (fallback goal hex color)
    checklist: pickChecklist(element.id, element.checklist, flatByParent),
    tasks: (tasksByParent.get(element.id) ?? []).map(buildTask),
  }))
}
